"use client";
import Image from "next/image";
import { Button } from "../../../ui/button";
import { FaLocationArrow } from "react-icons/fa";
import { MdStar } from "react-icons/md";
import Link from "next/link";
import { cn } from "../../../lib/utils";
import { DataState } from "../../../xui/data-state";
import { Skeleton } from "../../../ui/skeleton";
import type { Crop } from "@padi/shared/crops/";
import { fDMoney, fTon, replaceAndCap } from "@padi/lib/utils/format";
import { PaginationFooter } from "@padi/suites/pagination";
import type { PaginationProps } from "../../../lib/types";
import { useFeaturedCrops } from "../_hooks";

export const statusConfig: Record<
  Crop["status"],
  { label: string; className: string }
> = {
  active: {
    label: "Available",
    className: "bg-success text-white",
  },
  "sold-out": {
    label: "Sold Out",
    className: "bg-warning text-white",
  },
  inactive: {
    label: "Inactive",
    className: "bg-destructive text-white",
  },
  drafted: {
    label: "Drafted",
    className: "bg-destructive text-white",
  },
};

type CropListingProps = {
  crop: Crop;
  isOwnProfile?: boolean;
};
export const CropListing = ({ crop, isOwnProfile }: CropListingProps) => {
  const statusInfo = statusConfig[crop.status];
  // Use the first image from the images array, fallback to a placeholder
  const displayImage =
    crop.images?.[0]?.imageUrl || "/images/placeholder-crop.jpg";
  const altText = crop.images?.[0]?.altText || crop.name;

  // Format price with currency
  const formattedPrice = fDMoney(crop.displayMoney.pricePerTon);
  const { farmer } = crop;

  // Use farmer's rating data
  const rating = farmer?.averageRating || 0;
  const reviewCount = farmer?.totalRatingCount || 0;

  return (
    <div className="bg-white rounded-lg border border-muted overflow-hidden hover:shadow-md transition-shadow duration-200">
      {/* Image Container */}
      <div className="relative h-48 w-full">
        <Image
          src={displayImage}
          alt={altText}
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        />

        {/* Status Banner */}
        {crop.status && (
          <div className="absolute top-1.5 left-1.5">
            <span
              className={`px-3 py-1 rounded text-xs font-semibold ${statusInfo.className}`}
            >
              {replaceAndCap(crop.status)}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-2 space-y-2">
        {/* Product Name and Price */}
        <div className="flex justify-between items-start">
          <h3 className="font-semibold text-primary text-sm leading-tight flex-1 truncate">
            {crop.name}
          </h3>
          <span className="text-foreground font-semibold text-sm ml-2">
            {formattedPrice}
          </span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1">
          <div className="flex items-center">
            {[...Array(5)].map((_, i) => (
              <MdStar
                key={i}
                className={`size-4 ${
                  i < Math.floor(rating)
                    ? "text-[#E9B306] fill-[#E9B306]"
                    : "text-muted"
                }`}
              />
            ))}
          </div>
          <span className="text-foreground text-xs ml-1">
            ({reviewCount} reviews)
          </span>
        </div>

        {/* Availability & Location */}
        <div className="flex justify-between">
          <div className="text-muted-light text-sm">
            Available:{" "}
            <span className="font-bold">{fTon(crop.quantityInTon)}</span>
          </div>

          {/* Location */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-0.5">
              <FaLocationArrow className="size-3 text-primary" />
              <span className="text-foreground text-sm">
                {crop.city}, {crop.state}
              </span>
            </div>
          </div>
        </div>
        {/* Action Button */}
        <Link
          href={
            isOwnProfile
              ? `/home/listings/${crop.id}`
              : `/crops/${crop.slug || crop.id}`
          }
          className="block mt-2"
        >
          <Button
            fullWidth
            variant="secondary"
            size="lg"
            disabled={crop.status === "sold-out" && !isOwnProfile}
          >
            {isOwnProfile ? "Edit Listing" : "View Details"}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export const CropGridSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="bg-white max-w-80 rounded-lg border border-muted overflow-hidden"
      >
        <Skeleton className="h-48 w-full" />
        <div className="p-2 space-y-2">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    ))}
  </div>
);

type CropGridProps = {
  query: {
    data: Crop[] | undefined;
    isLoading: boolean;
    error?: Error | null;
    isError?: boolean;
    pagination?: PaginationProps;
    refetch?: () => void;
  };
  className?: string;
  isOwnProfile?: boolean;
  hidePagination?: boolean;
};

export const CropGrid = ({
  query,
  className,
  isOwnProfile,
  hidePagination,
}: CropGridProps) => {
  return (
    <DataState query={query} loading={<CropGridSkeleton />}>
      {(data: Crop[], pagination) => (
        <div className="space-y-6">
          <div
            className={cn(
              "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",
              className,
            )}
          >
            {data.map((crop) => (
              <CropListing
                key={crop.id}
                crop={crop}
                isOwnProfile={isOwnProfile}
              />
            ))}
          </div>
          {!hidePagination && <PaginationFooter pagination={pagination} />}
        </div>
      )}
    </DataState>
  );
};

export const CropSection = ({
  title,
  className,
  blackTitle,
}: {
  title: string;
  className?: string;
  blackTitle?: boolean;
}) => {
  const query = useFeaturedCrops();

  return (
    <section className={cn("py-16 bg-white", className)}>
      <div className="container px-4 sm:px-6 lg:px-8 space-y-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            className={cn(
              "text-3xl font-bold mb-4",
              blackTitle ? "text-foreground" : "text-secondary",
            )}
          >
            {title}
          </h2>
        </div>

        {/* Featured Crops Grid */}
        <CropGrid query={query} hidePagination />

        {/* View All Button */}
        <div className="text-center">
          <Link href="/crops">
            <Button size="xl" variant="accent">
              View all Listings
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};
